import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaUserCircle,
  FaTachometerAlt,
  FaUserEdit,
  FaTag,
  FaEnvelope,
  FaSignOutAlt,
  FaBars,
  FaTimes,
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import Logo from './Logo';

const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const userRoles = user?.roles || [];
  const isAdmin = userRoles.includes('ADMIN');
  const isSiteOwner = userRoles.includes('SITE_OWNER');
  const isUser = userRoles.includes('USER');
  const isSiteOwnerAndAdmin = isSiteOwner && isAdmin;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLivestreamDropdownOpen, setIsLivestreamDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Toggle the dropdown on click
  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Toggle the livestream dropdown on click
  const handleToggleLivestreamDropdown = () => {
    setIsLivestreamDropdownOpen((prev) => !prev);
  };

  // Close dropdown on outside click
  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Close livestream dropdown on outside click
  const handleCloseLivestreamDropdown = () => {
    setIsLivestreamDropdownOpen(false);
  };

  // Logout action
  const handleLogout = async (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <nav className="fixed top-0 left-1/2 transform -translate-x-1/2 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-lg w-[90%]" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)'}}>
      <div className="w-full">
        <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo / Brand */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="transition-transform duration-300 group-hover:scale-105">
              <Logo logoSrc="/1.svg" alt="Akiture Logo" size="medium" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/explore" 
              className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
            >
              Purchase
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/all-livestream-events" 
              className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
            >
              All Livestreams
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Pricing Link - Available to everyone (public page) */}
            <Link 
              to="/pricing" 
              className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
            >
              Pricing
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {user && isAdmin && (
              <Link 
                to="/admin/dashboard" 
                className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
              >
                Admin Dashboard
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            
            {(isUser || isAdmin) && (
              <Link 
                to="/dashboard" 
                className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
              >
                My Videos
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            
            {user && isSiteOwner && (
              <Link 
                to="/court-management" 
                className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm relative group"
              >
                Court Management
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            
            {user && (isSiteOwnerAndAdmin || isSiteOwner) && (
              <div className="relative">
                <button 
                  onClick={handleToggleLivestreamDropdown}
                  className="text-text-primary hover:text-primary transition-colors duration-300 font-medium text-sm flex items-center space-x-1"
                >
                  <span>Livestreams</span>
                  <svg className={`w-4 h-4 transition-transform duration-300 ${isLivestreamDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isLivestreamDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={handleCloseLivestreamDropdown}
                    ></div>
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <Link 
                        to="/all-livestream-events" 
                        onClick={handleCloseLivestreamDropdown}
                        className="block px-4 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg mx-2 my-1"
                      >
                        Manage Livestreams
                      </Link>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right side - User Menu / Login */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={handleToggleDropdown}
                  className="flex items-center space-x-3 p-2 rounded-full hover:bg-light-bg transition-colors duration-300 group"
                >
                  <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center text-white group-hover:scale-105 transition-transform duration-300">
                    <FaUserCircle className="w-6 h-6" />
                  </div>
                  <span className="hidden sm:block text-text-primary font-medium text-sm">{user.username}</span>
                  <svg 
                    className={`w-4 h-4 text-text-secondary transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={handleCloseDropdown}
                    ></div>
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-large border border-gray-200 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="px-6 py-4 bg-gradient-to-r from-light-bg to-white border-b border-gray-200">
                        <p className="text-xs text-text-secondary">Logged in as:</p>
                        <p className="text-sm font-semibold text-title">{user.username}</p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {isAdmin && (
                          <Link 
                            to="/admin/dashboard" 
                            onClick={handleCloseDropdown}
                            className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                          >
                            <FaTachometerAlt className="w-4 h-4" />
                            <span>Admin Dashboard</span>
                          </Link>
                        )}
                        
                        {(isUser || isAdmin) && (
                          <Link 
                            to="/dashboard" 
                            onClick={handleCloseDropdown}
                            className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                          >
                            <FaTachometerAlt className="w-4 h-4" />
                            <span>My Videos</span>
                          </Link>
                        )}

                        <Link 
                          to="/profile" 
                          onClick={handleCloseDropdown}
                          className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                        >
                          <FaUserEdit className="w-4 h-4" />
                          <span>Profile</span>
                        </Link>
                        
                        {user && (
                          <Link 
                            to="/account/subscription" 
                            onClick={handleCloseDropdown}
                            className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                          >
                            <FaTag className="w-4 h-4" />
                            <span>My Subscription</span>
                          </Link>
                        )}
                        
                        <Link 
                          to="/pricing" 
                          onClick={handleCloseDropdown}
                          className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                        >
                          <FaTag className="w-4 h-4" />
                          <span>Pricing</span>
                        </Link>
                        
                        {isAdmin && (
                          <Link 
                            to="/search-users" 
                            onClick={handleCloseDropdown}
                            className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                          >
                            <FaUserCircle className="w-4 h-4" />
                            <span>Search Users</span>
                          </Link>
                        )}
                        
                        <Link 
                          to="/contact" 
                          onClick={handleCloseDropdown}
                          className="flex items-center space-x-3 px-6 py-3 text-sm text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300"
                        >
                          <FaEnvelope className="w-4 h-4" />
                          <span>Contact Support</span>
                        </Link>

                        <div className="border-t border-gray-200 my-2"></div>
                        
                        <button 
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-6 py-3 text-sm text-error hover:bg-red-50 hover:text-red-700 transition-colors duration-300 w-full text-left"
                        >
                          <FaSignOutAlt className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link 
                to="/login" 
                className="bg-secondary text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:shadow-medium hover:scale-105 transition-all duration-300"
              >
                Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-light-bg transition-colors duration-300"
            >
              {isMobileMenuOpen ? (
                <FaTimes className="w-6 h-6 text-text-primary" />
              ) : (
                <FaBars className="w-6 h-6 text-text-primary" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-2">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
              >
                Home
              </Link>
              <Link 
                to="/explore" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
              >
                Purchase
              </Link>
              <Link 
                to="/all-livestream-events" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
              >
                All Livestreams
              </Link>
              
              <Link 
                to="/pricing" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
              >
                Pricing
              </Link>
              
              {user && isAdmin && (
                <Link 
                  to="/admin/dashboard" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
                >
                  Admin Dashboard
                </Link>
              )}
              
              {(isUser || isAdmin) && (
                <Link 
                  to="/dashboard" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
                >
                  My Videos
                </Link>
              )}
              
              {user && isSiteOwner && (
                <Link 
                  to="/court-management" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
                >
                  Court Management
                </Link>
              )}
              
              {user && (isSiteOwnerAndAdmin || isSiteOwner) && (
                <Link 
                  to="/all-livestream-events" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-text-primary hover:bg-light-bg hover:text-primary transition-colors duration-300 rounded-lg"
                >
                  Manage Livestreams
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;